import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { Plus, CheckSquare, Square, Trash2, Folder, X } from '@/ui/icons'
import { GpxImporter, AddWaypointToTripPanel, WaypointFolderTree, BulkMoveWaypointsDialog } from '@/pages/itinerary-builder/components'
import { WaypointResponse } from '@/shared/api/routes'
import { RoutesApi } from '@/shared/api/routes'
import { useAuth } from '@/modules/auth'
import { Button } from '@/ui/components'
import { useConfirm } from '@/shared/hooks'
import { ConfirmationDialog } from '@/ui/components'
import { useWaypointFolders } from '@/modules/routes/hooks/useWaypointFolders'

interface LibraryPanelProps {
  selectedWaypoint?: WaypointResponse | null
  onWaypointClick?: (waypoint: WaypointResponse) => void
  onFolderWaypointsFocus?: (waypoints: WaypointResponse[], folderId: string) => void
  onImportComplete?: () => void
  onWaypointAddedToTrip?: () => void
  refreshTrigger?: number // When this changes, refresh the waypoints list
}

export function LibraryPanel({
  selectedWaypoint,
  onWaypointClick,
  onFolderWaypointsFocus,
  onImportComplete,
  onWaypointAddedToTrip,
  refreshTrigger
}: LibraryPanelProps) {
  const { getAccessToken } = useAuth()
  const [libraryWaypoints, setLibraryWaypoints] = useState<WaypointResponse[]>([])
  const [loadingWaypoints, setLoadingWaypoints] = useState(false)
  const [selectedWaypointForAdd, setSelectedWaypointForAdd] = useState<WaypointResponse | null>(null)
  const [deletingWaypointId, setDeletingWaypointId] = useState<string | null>(null)
  const [createFolderTrigger, setCreateFolderTrigger] = useState(0)
  const [selectionMode, setSelectionMode] = useState(false)
  const [selectedWaypointIds, setSelectedWaypointIds] = useState<Set<string>>(new Set())
  const [showBulkMoveDialog, setShowBulkMoveDialog] = useState(false)
  const { confirm, confirmState, close, setLoading: setConfirmLoading } = useConfirm()
  
  const routesApi = React.useMemo(() => new RoutesApi(getAccessToken), [getAccessToken])
  const { folders: foldersFromHook, moveWaypointToFolder, refreshFolders } = useWaypointFolders({ getAccessToken })
  
  // Local state for optimistic updates - use this instead of hook's folders when available
  const [optimisticFolders, setOptimisticFolders] = useState<typeof foldersFromHook | null>(null)
  const folders = optimisticFolders || foldersFromHook
  
  // Sync optimistic folders with hook's folders when they update
  useEffect(() => {
    if (!optimisticFolders) {
      // No optimistic updates, use hook's folders
      return
    }
    // If hook's folders update (e.g., from background refresh), sync optimistic state
    // This ensures we don't lose server updates
    setOptimisticFolders(foldersFromHook)
  }, [foldersFromHook])
  
  // Helper function to recursively find and update a folder in the tree
  const updateFolderInTree = useCallback((
    tree: typeof folders,
    folderId: string,
    updater: (folder: typeof folders[0]) => typeof folders[0]
  ): typeof folders => {
    return tree.map(folder => {
      if (folder.id === folderId) {
        return updater(folder)
      }
      if (folder.children.length > 0) {
        return {
          ...folder,
          children: updateFolderInTree(folder.children, folderId, updater)
        }
      }
      return folder
    })
  }, [])
  
  // Helper function to replace a folder by ID (useful for replacing temp IDs with real IDs)
  const replaceFolderInTree = useCallback((
    tree: typeof folders,
    oldId: string,
    newFolder: typeof folders[0]
  ): typeof folders => {
    return tree.map(folder => {
      if (folder.id === oldId) {
        return newFolder
      }
      if (folder.children.length > 0) {
        return {
          ...folder,
          children: replaceFolderInTree(folder.children, oldId, newFolder)
        }
      }
      return folder
    })
  }, [])
  
  // Helper function to recursively find and remove a folder from the tree
  const removeFolderFromTree = useCallback((
    tree: typeof folders,
    folderId: string
  ): typeof folders => {
    return tree
      .filter(folder => folder.id !== folderId)
      .map(folder => ({
        ...folder,
        children: removeFolderFromTree(folder.children, folderId)
      }))
  }, [])
  
  // Helper function to add a folder to the tree
  const addFolderToTree = useCallback((
    tree: typeof folders,
    newFolder: typeof folders[0],
    parentId: string | null
  ): typeof folders => {
    if (parentId === null) {
      // Add to root
      return [...tree, newFolder]
    }
    // Find parent and add as child
    return tree.map(folder => {
      if (folder.id === parentId) {
        return {
          ...folder,
          children: [...folder.children, newFolder]
        }
      }
      if (folder.children.length > 0) {
        return {
          ...folder,
          children: addFolderToTree(folder.children, newFolder, parentId)
        }
      }
      return folder
    })
  }, [])
  
  // Optimistic update functions
  const optimisticCreateFolder = useCallback((newFolder: typeof folders[0], parentId: string | null) => {
    setOptimisticFolders(prev => {
      const current = prev || foldersFromHook
      return addFolderToTree(current, newFolder, parentId)
    })
  }, [foldersFromHook, addFolderToTree])
  
  const optimisticUpdateFolder = useCallback((folderId: string, updates: Partial<typeof folders[0]>) => {
    setOptimisticFolders(prev => {
      const current = prev || foldersFromHook
      
      // If the ID changed (e.g., replacing temp ID with real ID), we need to replace the folder
      if (updates.id && updates.id !== folderId) {
        // Find the folder with the old ID
        const findFolder = (tree: typeof folders, id: string): typeof folders[0] | null => {
          for (const folder of tree) {
            if (folder.id === id) return folder
            if (folder.children.length > 0) {
              const found = findFolder(folder.children, id)
              if (found) return found
            }
          }
          return null
        }
        
        const oldFolder = findFolder(current, folderId)
        if (oldFolder) {
          // Create new folder with updated fields
          const newFolder = { ...oldFolder, ...updates } as typeof folders[0]
          // Replace the old folder with the new one in the tree
          return replaceFolderInTree(current, folderId, newFolder)
        }
        return current
      }
      
      // Normal update (no ID change)
      return updateFolderInTree(current, folderId, folder => ({ ...folder, ...updates }))
    })
  }, [foldersFromHook, updateFolderInTree, replaceFolderInTree])
  
  const optimisticDeleteFolder = useCallback((folderId: string) => {
    setOptimisticFolders(prev => {
      const current = prev || foldersFromHook
      return removeFolderFromTree(current, folderId)
    })
  }, [foldersFromHook, removeFolderFromTree])
  
  // Force re-render key to ensure tree rebuilds after moves
  const [treeKey, setTreeKey] = useState(0)

  // Fetch all library waypoints (waypoints with folder_id - not trip-specific)
  const fetchLibraryWaypoints = useCallback(async (bypassCache = false) => {
    try {
      setLoadingWaypoints(true)
      // Fetch all user waypoints - library waypoints are those with folder_id (not null)
      // Trip-specific waypoints have folder_id = null and should not appear in library
      const allWaypoints = await routesApi.getWaypoints({}, bypassCache ? { cache: 'no-store' } : undefined)
      // Filter for library waypoints (folder_id IS NOT NULL) - trip-specific waypoints have folder_id = null
      const libraryWps = allWaypoints.filter(wp => wp.folder_id !== null && wp.folder_id !== undefined)
      setLibraryWaypoints(libraryWps)
    } catch (error) {

    } finally {
      setLoadingWaypoints(false)
    }
  }, [routesApi])

  // Initial fetch
  useEffect(() => {
    fetchLibraryWaypoints()
  }, [fetchLibraryWaypoints])

  // Refresh when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      fetchLibraryWaypoints(true) // bypassCache = true
      refreshFolders()
      setTreeKey(prev => prev + 1)
    }
  }, [refreshTrigger, fetchLibraryWaypoints, refreshFolders])

  // Refresh waypoints after import
  const handleImportComplete = useCallback(async () => {
    // Small delay to ensure backend has processed the import
    await new Promise(resolve => setTimeout(resolve, 300))
    
    // Refresh both waypoints and folders to update counts and show new waypoints
    await Promise.all([
      refreshFolders(),
      fetchLibraryWaypoints(true) // bypassCache = true
    ])
    
    // Force tree to rebuild
    setTreeKey(prev => prev + 1)
    
    if (onImportComplete) {
      onImportComplete()
    }
    
    // Do a second refresh after a short delay to catch any eventual consistency issues
    setTimeout(async () => {
      await Promise.all([
        refreshFolders(),
        fetchLibraryWaypoints(true)
      ])
      setTreeKey(prev => prev + 1)
    }, 1000)
  }, [fetchLibraryWaypoints, refreshFolders, onImportComplete])

  // Handle waypoint moved (from drag and drop or single move)
  const handleWaypointMoved = useCallback(async () => {
    await Promise.all([
      refreshFolders(),
      fetchLibraryWaypoints()
    ])
    // Force tree to rebuild
    setTreeKey(prev => prev + 1)
  }, [fetchLibraryWaypoints, refreshFolders])

  // Handle opening Add to Trip panel
  const handleOpenAddToTrip = useCallback((waypoint: WaypointResponse) => {
    setSelectedWaypointForAdd(waypoint)
  }, [])

  const handleWaypointClick = useCallback((waypoint: WaypointResponse) => {
    if (onWaypointClick) {
      onWaypointClick(waypoint)
    }
  }, [onWaypointClick])

  const handleWaypointAddToTrip = useCallback((waypoint: WaypointResponse) => {
    setSelectedWaypointForAdd(waypoint)
  }, [])

  const handleFolderClick = useCallback(
    (folderId: string) => {
      const findFolderById = (
        tree: typeof folders,
        targetFolderId: string
      ): (typeof folders)[number] | null => {
        for (const folder of tree) {
          if (folder.id === targetFolderId) return folder
          if (folder.children?.length) {
            const nested = findFolderById(folder.children, targetFolderId)
            if (nested) return nested
          }
        }
        return null
      }

      const collectFolderIds = (folder: (typeof folders)[number]): Set<string> => {
        const folderIds = new Set<string>([folder.id])
        folder.children?.forEach((child) => {
          collectFolderIds(child).forEach((id) => folderIds.add(id))
        })
        return folderIds
      }

      const selectedFolder = findFolderById(folders, folderId)
      if (!selectedFolder) {
        onFolderWaypointsFocus?.([], folderId)
        return
      }

      const targetFolderIds = collectFolderIds(selectedFolder)
      const folderWaypoints = libraryWaypoints.filter((waypoint) => {
        if (!waypoint.folder_id) return false
        return targetFolderIds.has(waypoint.folder_id)
      })

      onFolderWaypointsFocus?.(folderWaypoints, folderId)
    },
    [folders, libraryWaypoints, onFolderWaypointsFocus]
  )

  const handleWaypointDelete = useCallback(async (waypoint: WaypointResponse) => {
    await confirm(
      {
        title: 'Delete Waypoint',
        description: `Are you sure you want to delete "${waypoint.name || 'Unnamed Waypoint'}"? This action cannot be undone.`,
        variant: 'destructive',
        confirmText: 'Delete',
      },
      async () => {
        setDeletingWaypointId(waypoint.id)
        setConfirmLoading(true)
        try {
          await routesApi.deleteWaypoint(waypoint.id)
          
          // Small delay to ensure backend has processed deletion
          await new Promise(resolve => setTimeout(resolve, 200))
          
          // Refresh both waypoints and folders to update counts
          await Promise.all([
            refreshFolders(),
            fetchLibraryWaypoints(true) // bypassCache = true
          ])
          
          // Force tree to rebuild
          setTreeKey(prev => prev + 1)

        } catch (error) {

          throw error
        } finally {
          setDeletingWaypointId(null)
          setConfirmLoading(false)
        }
      }
    )
  }, [confirm, routesApi, fetchLibraryWaypoints, refreshFolders, setConfirmLoading])

  // Selection mode handlers
  const handleToggleSelectionMode = useCallback(() => {
    setSelectionMode((prev) => {
      if (!prev) {
        // Entering selection mode - clear selections
        setSelectedWaypointIds(new Set())
      }
      return !prev
    })
  }, [])

  const handleToggleWaypointSelection = useCallback((waypointId: string, checked: boolean) => {
    setSelectedWaypointIds((prev) => {
      const next = new Set(prev)
      if (checked) {
        next.add(waypointId)
      } else {
        next.delete(waypointId)
      }
      return next
    })
  }, [])

  const handleSelectAll = useCallback(() => {
    if (selectedWaypointIds.size === libraryWaypoints.length) {
      // Deselect all
      setSelectedWaypointIds(new Set())
    } else {
      // Select all
      setSelectedWaypointIds(new Set(libraryWaypoints.map((wp) => wp.id)))
    }
  }, [libraryWaypoints, selectedWaypointIds.size])

  // Bulk operations
  const handleBulkDelete = useCallback(async () => {
    const selectedCount = selectedWaypointIds.size
    if (selectedCount === 0) return

    await confirm(
      {
        title: 'Delete Waypoints',
        description: `Are you sure you want to delete ${selectedCount} waypoint${selectedCount !== 1 ? 's' : ''}? This action cannot be undone.`,
        variant: 'destructive',
        confirmText: 'Delete',
      },
      async () => {
        setConfirmLoading(true)
        try {
          const waypointIdsArray = Array.from(selectedWaypointIds)
          // Delete all waypoints in parallel
          await Promise.all(waypointIdsArray.map((id) => routesApi.deleteWaypoint(id)))
          
          // Small delay to ensure backend has processed deletions
          await new Promise(resolve => setTimeout(resolve, 300))
          
          // Refresh both waypoints and folders to update counts
          await Promise.all([
            refreshFolders(),
            fetchLibraryWaypoints(true) // bypassCache = true
          ])
          
          // Force tree to rebuild
          setTreeKey(prev => prev + 1)
          
          setSelectedWaypointIds(new Set())
          setSelectionMode(false)

          // Do a second refresh after a short delay to catch any eventual consistency issues
          setTimeout(async () => {
            await Promise.all([
              refreshFolders(),
              fetchLibraryWaypoints(true)
            ])
            setTreeKey(prev => prev + 1)
          }, 1000)
        } catch (error) {

          throw error
        } finally {
          setConfirmLoading(false)
        }
      }
    )
  }, [selectedWaypointIds, confirm, routesApi, fetchLibraryWaypoints, refreshFolders, setConfirmLoading])

  const handleBulkMove = useCallback(async (waypointIds: string[], folderId: string | null) => {
    try {
      // Optimistically update waypoints in local state immediately
      setLibraryWaypoints((prev) => 
        prev.map((wp) => 
          waypointIds.includes(wp.id) 
            ? { ...wp, folder_id: folderId }
            : wp
        )
      )
      
      // Force tree to rebuild immediately with optimistic update
      setTreeKey(prev => prev + 1)
      
      // Move all waypoints in parallel
      await Promise.all(waypointIds.map((id) => moveWaypointToFolder(id, folderId)))
      
      // Wait a bit longer to ensure backend has fully processed all updates
      await new Promise(resolve => setTimeout(resolve, 500))
      
      // Refresh both waypoints and folders to ensure UI is up to date with server state
      // Use cache: 'no-store' to ensure we get fresh data
      await Promise.all([
        refreshFolders(),
        fetchLibraryWaypoints(true) // bypassCache = true
      ])
      
      // Force tree to rebuild again with fresh data
      setTreeKey(prev => prev + 1)
      
      // Do a second refresh after a short delay to catch any eventual consistency issues
      setTimeout(async () => {
        await Promise.all([
          refreshFolders(),
          fetchLibraryWaypoints(true)
        ])
        setTreeKey(prev => prev + 1)
      }, 1000)
      
      setSelectedWaypointIds(new Set())
      setSelectionMode(false)
      
      // Get folder name - need to wait for folders to update
      // Use a small delay to ensure folders state has updated
      setTimeout(() => {
        const folderName = folderId 
          ? (() => {
              const findFolder = (id: string, list: typeof folders): typeof folders[0] | null => {
                for (const folder of list) {
                  if (folder.id === id) return folder
                  if (folder.children.length > 0) {
                    const found = findFolder(id, folder.children)
                    if (found) return found
                  }
                }
                return null
              }
              return findFolder(folderId, folders)?.name || 'folder'
            })()
          : 'No Folder'

      }, 100)
    } catch (error) {
      // On error, revert optimistic update by refreshing with cache bypass
      await fetchLibraryWaypoints(true)
      setTreeKey(prev => prev + 1)

      throw error
    }
  }, [moveWaypointToFolder, fetchLibraryWaypoints, refreshFolders, folders])

  const selectedCount = selectedWaypointIds.size
  const allSelected = libraryWaypoints.length > 0 && selectedCount === libraryWaypoints.length

  return (
    <div className="h-full min-w-0 flex flex-col">
      {/* Header with Create Folder and Select buttons */}
      <div className="flex flex-wrap items-center justify-end gap-2 p-3 border-b">
        {selectionMode ? (
          <Button
            size="sm"
            variant="outline"
            onClick={handleToggleSelectionMode}
            className="h-7 gap-1.5 px-2 text-xs"
          >
            <X className="w-3.5 h-3.5" />
            Cancel
          </Button>
        ) : (
          <>
            <Button
              size="sm"
              variant="outline"
              onClick={handleToggleSelectionMode}
              className="h-7 gap-1.5 px-2 text-xs"
            >
              <CheckSquare className="w-3.5 h-3.5" />
              Select
            </Button>
            <Button
              size="sm"
              onClick={() => setCreateFolderTrigger((prev) => prev + 1)}
              className="h-7 gap-1.5 px-2 text-xs"
            >
              <Plus className="w-3.5 h-3.5" />
              Create Folder
            </Button>
          </>
        )}
      </div>

      {/* Bulk Action Bar */}
      {selectionMode && selectedCount > 0 && (
        <div className="flex items-center justify-between p-3 border-b bg-adventure-50">
          <div className="flex items-center gap-3">
            <Button
              size="sm"
              variant="outline"
              onClick={handleSelectAll}
              className="flex items-center gap-2"
            >
              {allSelected ? (
                <>
                  <Square className="w-4 h-4" />
                  Deselect All
                </>
              ) : (
                <>
                  <CheckSquare className="w-4 h-4" />
                  Select All
                </>
              )}
            </Button>
            <span className="text-sm text-gray-600">
              {selectedCount} waypoint{selectedCount !== 1 ? 's' : ''} selected
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowBulkMoveDialog(true)}
              className="flex items-center gap-2"
            >
              <Folder className="w-4 h-4" />
              Move to Folder
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={handleBulkDelete}
              className="flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </Button>
          </div>
        </div>
      )}

      <div className="flex-1 min-w-0 overflow-y-auto p-4 space-y-6">
        {/* GPX Importer Section */}
        <div>
          <GpxImporter onImportComplete={handleImportComplete} />
        </div>

        {/* Folder Tree with Waypoints */}
        {loadingWaypoints ? (
          <div className="text-center py-8 text-gray-500">
            <p className="text-sm">Loading waypoints...</p>
          </div>
        ) : (
          <WaypointFolderTree
            key={treeKey}
            waypoints={libraryWaypoints}
            folders={folders}
            selectedWaypoint={selectedWaypoint}
            onWaypointClick={handleWaypointClick}
            onFolderClick={handleFolderClick}
            onWaypointMoved={handleWaypointMoved}
            onWaypointAddToTrip={handleWaypointAddToTrip}
            onWaypointDelete={handleWaypointDelete}
            deletingWaypointId={deletingWaypointId}
            onCreateFolderClick={createFolderTrigger > 0 ? createFolderTrigger : undefined}
            selectionMode={selectionMode}
            selectedWaypointIds={selectedWaypointIds}
            onToggleWaypointSelection={handleToggleWaypointSelection}
            onFolderCreated={async () => {
              // Reset the create folder trigger first to prevent dialog from reopening on remount
              setCreateFolderTrigger(0)
              // Refresh folders in background to sync with server (for waypoint counts, etc.)
              // Optimistic updates handle the immediate UI changes
              await refreshFolders()
              // Sync optimistic state with server state
              setOptimisticFolders(null) // Reset to use hook's folders
            }}
            onOptimisticCreateFolder={optimisticCreateFolder}
            onOptimisticUpdateFolder={optimisticUpdateFolder}
            onOptimisticDeleteFolder={optimisticDeleteFolder}
          />
        )}
      </div>

      {/* Add Waypoint to Trip Panel */}
      {selectedWaypointForAdd && (
        <AddWaypointToTripPanel
          waypoint={selectedWaypointForAdd}
          onClose={() => setSelectedWaypointForAdd(null)}
          onSuccess={async () => {
            await fetchLibraryWaypoints()
            if (onWaypointAddedToTrip) {
              onWaypointAddedToTrip()
            }
            setSelectedWaypointForAdd(null)
          }}
        />
      )}

      {/* Bulk Move Dialog */}
      <BulkMoveWaypointsDialog
        open={showBulkMoveDialog}
        onOpenChange={setShowBulkMoveDialog}
        waypointIds={Array.from(selectedWaypointIds)}
        folders={folders}
        onMove={handleBulkMove}
        onSuccess={() => {
          setShowBulkMoveDialog(false)
        }}
      />

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmState?.open ?? false}
        onOpenChange={(open) => !open && close()}
        title={confirmState?.title}
        description={confirmState?.description ?? ''}
        confirmText={confirmState?.confirmText}
        cancelText={confirmState?.cancelText}
        variant={confirmState?.variant}
        onConfirm={confirmState?.onConfirm ?? (() => {})}
        isLoading={confirmState?.isLoading}
      />
    </div>
  )
}

